import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import {
  CalendarQueryVariables,
  FlightOptionsQueryVariables,
  Maybe,
  Offer,
  Receipt,
  useReceiptsQuery,
} from '@gql/schema';
import {
  ElementName,
  ElementType,
  buildGroupsAndPeopleFromSelection,
  createQueryParamFromSelection,
} from '@secretescapes/cauldron';
import { i18n } from '@secretescapes/i18n';
import * as Sentry from '@sentry/react';
import { createMomentFromParam, formatMomentToString, isSameDay } from '@utils/dates.utils';
import { dispatchTrackingEvent } from '@utils/tracking.utils';
import { setURLParams } from '@utils/url.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useServerState } from '@utils/useServerState';
import { useTranslations } from '@utils/useTranslations';
import moment from 'moment';
import {
  FlightsSelectOption,
  IFlightOffer,
  getAirportOptions,
  getNightOptions,
  useFlightState,
} from '@features/Flights';
import {
  AvailabilityPresentationHandle,
  DateSelectionTrackingDetails,
  FocusedInput,
  TFocusedInput,
} from './AvailabilityPresentation.interfaces';
import { fetchFlightOptions } from './AvailabilityPresentation.queries';
import {
  OffersWrapper,
  Presentation,
  RetryBoxWrapper,
  SelectionWrapper,
  StyledRetryBox,
} from './AvailabilityPresentation.styles';
import {
  ALL_OFFERS,
  deleteURLParams,
  determineMaxRoomCapacity,
  getCheapestFinalRates,
  getCheapestFlightOptions,
  getURLParams,
  notEmpty,
  urlParamsExist,
} from './AvailabilityPresentation.utils';
import { AvailabilitySearchBar } from './AvailabilitySearchBar';
import { Option } from './AvailabilitySearchBar/DropdownInput';
import { NullableMoment } from './Calendar';
import { CreditsStaffDiscountInfoBox } from './CreditsStaffDiscounts/CreditsStaffDiscountInfoBox';
import { navigatePostSale } from './Navigation.utils';
import { Offers } from './Offers';
import { useSelectionActions, useSelectionState } from './SelectionContext';
import FlightsSelectTabs from './Tabs/FlightSelectTabs';
import { useData } from './useData';

interface Props {
  flightPriceAvailable: boolean;
  scrollToSection?: (isStickyNavInactive?: boolean) => void;
}

export const AvailabilityPresentation = forwardRef<AvailabilityPresentationHandle, Props>(
  ({ flightPriceAvailable, scrollToSection }: Props, ref) => {
    const { tripTypeParam, roomsParam, durationParam, airportCodeParam, airportNameParam } =
      getURLParams();

    const client = useApolloClient();
    const { saleId, setFinalRates } = useAppState();
    const { sale } = useServerState();
    const { territoryCode, affiliateCode, territoryLocale, currency, isMobile } =
      useApplicationState();
    const { translations } = useTranslations();
    const bookingFeesEnabled = useFeatureFlag(BullfinchFeatureFlags.bookingFeesEnabled);
    const flightsEnabled = useFeatureFlag(BullfinchFeatureFlags.flightsEnabled);
    const {
      flightState,
      flightsSelected,
      setFlightsSelected,
      setFlightState,
      withFlightsDescription,
      withFlightsOffers,
    } = useFlightState({
      flightsEnabled,
      sale,
      translations,
      bypassInitialDuration: true,
      tripTypeParam,
      durationParam,
      airportCodeParam,
      airportNameParam,
    });
    const checkInDateParam = createMomentFromParam('checkin', territoryLocale);
    const checkOutDateParam = createMomentFromParam('checkout', territoryLocale);

    const { offerId, travellersSelection } = useSelectionState();
    const { changeOfferId } = useSelectionActions();

    const [checkInDate, setCheckInDate] = useState<NullableMoment>(
      checkInDateParam?.clone() ?? null
    );
    const [checkOutDate, setCheckOutDate] = useState<NullableMoment>(
      checkOutDateParam?.clone() ?? null
    );

    const [numOfRooms, setNumOfRooms] = useState(roomsParam ?? 1);
    const [focusedInput, setFocusedInput] = useState<TFocusedInput>(null);
    const [animateOffersSelect, setAnimateOffersSelect] = useState(false);

    const offerCardDetailsEnabled = useFeatureFlag(BullfinchFeatureFlags.offerCardDetailsEnabled);
    const travellersHOSelectionEnabled = useFeatureFlag(
      BullfinchFeatureFlags.travellersHOSelection
    );

    const isRelevantTravellersSelectionEnabled =
      flightsSelected || (!flightsSelected && travellersHOSelectionEnabled);

    let currentNumberOfRooms: number;

    if (flightsSelected) {
      currentNumberOfRooms = travellersSelection.rooms.length;
    } else {
      if (travellersHOSelectionEnabled) {
        currentNumberOfRooms = travellersSelection.rooms.length;
      } else {
        currentNumberOfRooms = numOfRooms;
      }
    }

    const dateSearchInitialised = useRef(false);

    const presentationContainerRef = useRef<HTMLDivElement | null>(null);

    const { people, groups } = buildGroupsAndPeopleFromSelection(travellersSelection);

    const queryVariables: CalendarQueryVariables = useMemo(() => {
      return {
        saleId,
        affiliate: affiliateCode,
        territory: territoryCode,
        packageGroups: offerId !== ALL_OFFERS ? [offerId] : [],
        ...(isRelevantTravellersSelectionEnabled
          ? { people, groups }
          : { rooms: currentNumberOfRooms }),
        nights:
          flightsSelected && flightState.selectedNights.id ? [flightState.selectedNights.id] : [],
        departureAirports: flightsSelected ? [flightState.selectedAirport.id] : [],
      };
    }, [
      saleId,
      affiliateCode,
      territoryCode,
      offerId,
      flightsSelected,
      flightState,
      currentNumberOfRooms,
      people,
      groups,
      isRelevantTravellersSelectionEnabled,
    ]);

    const { availability, offers, errors, refetch } = useData({
      queryVariables,
      withFlightsOffers,
      flightsSelected,
      onFlightStateChange: setFlightState,
    });

    const { data: receiptsData, loading: receiptsLoading } = useReceiptsQuery({
      skip: !checkInDateParam || !checkOutDateParam,
      context: {
        headers: {
          ...(!flightsSelected && bookingFeesEnabled
            ? { 'x-ff-tb': BullfinchFeatureFlags.bookingFeesEnabled }
            : {}),
        },
      },
      variables: {
        saleId,
        tracyOfferId: sale?.tracyOfferId ?? '',
        territory: territoryCode,
        date: formatMomentToString(checkInDateParam),
        nights: moment(checkOutDateParam)
          .startOf('d')
          .diff(moment(checkInDateParam).startOf('d'), 'd'),
        ...(isRelevantTravellersSelectionEnabled
          ? { people, groups }
          : { rooms: currentNumberOfRooms }),
        departureAirports:
          flightsSelected && flightState.selectedAirport.id
            ? [flightState.selectedAirport.id as string]
            : [],
      },
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        const offersWithReceipts = data.sale?.offers?.filter(offer => !!offer?.receipt) ?? [];

        dispatchTrackingEvent<DateSelectionTrackingDetails>({
          name: 'dateSelection',
          contentElement: {
            elementName: 'offers' as ElementName,
            elementType: ElementType.NON_EXPIRED_SALE,
          },
          details: {
            specificDatesSearch: true,
            triggeredBy: 'sale_page_user',
            numResults: offersWithReceipts.length,
            parameters: {
              airport: flightsSelected ? flightState.selectedAirport.title : '',
              checkInDate: formatMomentToString(checkInDateParam),
              checkOutDate: formatMomentToString(checkOutDateParam),
              travellersSelection: isRelevantTravellersSelectionEnabled
                ? createQueryParamFromSelection(travellersSelection)
                : '',
            },
            results: offersWithReceipts.map(offerWithReceipt => ({
              offerId: offerWithReceipt?.id ?? '',
              price: offerWithReceipt?.receipt?.amountForDisplay ?? '',
              source: 'offerSearch',
            })),
          },
        });

        const receipts = offersWithReceipts.map(or => or?.receipt).filter(notEmpty);

        const rates = getCheapestFinalRates(
          receipts,
          flightsSelected ? 1 : currentNumberOfRooms,
          currency,
          territoryLocale
        );

        setFinalRates(prev => ({
          ...prev,
          show: true,
          cheapestFinalRate: rates?.cheapestFinalRate ?? '',
          cheapestFinalRatePerRoomPerNight: rates?.cheapestFinalRatePerRoomPerNight ?? '',
          numberOfTravellers: people.length,
          numberOfNights: receipts[0]?.nights ?? 1,
        }));
      },
    });

    const transformedOffers: Offer[] = useMemo(() => {
      const receipts = (receiptsData?.sale?.offers?.filter(notEmpty) ?? []).reduce(
        (acc, item) => {
          acc[item.id] = item.receipt;
          return acc;
        },
        {} as Record<string, Maybe<Receipt>>
      );

      return offers.data.map(o => ({
        ...o,
        receipt: receipts[o.id],
      }));
    }, [offers.data, receiptsData]);

    const getFlightOptions = useCallback(
      async (newAirportId: string, newDurationId: number) => {
        const variables: FlightOptionsQueryVariables = {
          saleId,
          affiliate: affiliateCode,
          territory: territoryCode,
          flightsEnabled: true,
          departureAirportCode: newAirportId,
          duration: newDurationId,
        };

        const queries = transformedOffers.map(offer =>
          fetchFlightOptions(client, { ...variables, offerId: offer.id })
        );

        const queryResults = await Promise.all(queries);

        const { flightOffers, airports, durations } = getCheapestFlightOptions(queryResults);

        setFlightState(current => ({
          ...current,
          airports: getAirportOptions(airports, translations),
          nights: getNightOptions(durations, translations),
          offers: flightOffers as IFlightOffer[],
        }));

        // Reset offer id when current offer id is not available with flights selection
        if (isMobile && offerId !== ALL_OFFERS) {
          const selectedOfferExists = flightOffers.find(o => o.offerId === offerId);
          if (!selectedOfferExists) {
            changeOfferId(ALL_OFFERS);
          }
        }
      },
      [
        affiliateCode,
        changeOfferId,
        client,
        isMobile,
        offerId,
        transformedOffers,
        saleId,
        setFlightState,
        territoryCode,
        translations,
      ]
    );

    useImperativeHandle(
      ref,
      () => ({
        openCalendar: () => {
          setFocusedInput(FocusedInput.StartDate);
        },
        container: presentationContainerRef,
      }),
      []
    );

    const handleAnimationChange = (newValue: boolean) => setAnimateOffersSelect(newValue);

    const openCalendar = (selectedOfferId: string) => {
      if (!isMobile) {
        scrollToSection?.();
      }

      changeOfferId(selectedOfferId);
      setAnimateOffersSelect(true);

      setFocusedInput(FocusedInput.StartDate);
    };

    const handleRetry = () => {
      if (urlParamsExist()) {
        deleteURLParams();
        window?.location?.reload();
      } else {
        void refetch();
      }
    };

    const offerOptions: Option[] = [
      { value: ALL_OFFERS, text: i18n('calendar.offer.dropdown.alloffers', [], translations) },
      ...transformedOffers
        .filter(offer => currentNumberOfRooms <= (offer?.prices?.maxAvailability ?? 0))
        .map(offer => ({
          value: offer.id,
          text: offer.title ?? '',
        })),
    ];

    const roomTypePlural = transformedOffers[0]?.room?.roomTypePlural ?? '';
    const roomType = transformedOffers[0]?.room?.roomType ?? '';

    // A way to remove this useEffect in the future
    useEffect(() => {
      if (!dateSearchInitialised.current && !checkInDate) {
        setFinalRates(prev => ({ ...prev, flightsSelected }));
        dateSearchInitialised.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (offers.loading) {
      return (
        <Skeleton
          flightPriceAvailable={flightPriceAvailable}
          showOfferDetailsContent={!offerCardDetailsEnabled}
          isMobile={!!isMobile}
        />
      );
    }

    if (
      (errors.length || !transformedOffers.length) &&
      (!withFlightsOffers || Object.keys(withFlightsOffers).length)
    ) {
      return (
        <RetryBoxWrapper data-testid="retry-box-avail-pres">
          <StyledRetryBox retryFunction={handleRetry} />
        </RetryBoxWrapper>
      );
    }

    const handleDatesChange = (
      changedCheckInDate: NullableMoment,
      changedCheckOutDate: NullableMoment
    ) => {
      setCheckInDate(changedCheckInDate);
      setCheckOutDate(changedCheckOutDate);
    };

    const isSameDates =
      checkInDate?.isSame(checkInDateParam, 'd') && checkOutDate?.isSame(checkOutDateParam, 'd');

    const onBookButtonClicked = (selectedOfferId: string) => {
      if (!checkInDate || !checkOutDate || !saleId || !selectedOfferId) {
        return;
      }

      const clickedOffer = transformedOffers.find(offer => offer.id === selectedOfferId);

      if (!clickedOffer) {
        return;
      }

      const duration = moment(checkOutDate)
        .startOf('d')
        .diff(moment(checkInDate).startOf('d'), 'd');

      const checkInLabel = checkInDate.locale(territoryLocale).format('YYYY-MM-DD');

      const checkOutLabel = checkOutDate.locale(territoryLocale).format('YYYY-MM-DD');

      navigatePostSale({
        withFlights: flightsSelected,
        startDate: checkInLabel,
        endDate: checkOutLabel,
        offerId: selectedOfferId,
        flightState,
        saleId,
        totalPrice: clickedOffer.receipt?.amount ?? 0,
        numberOfRooms: currentNumberOfRooms,
        duration,
        rateCode: clickedOffer.rateCode ?? '', // validate rate code
        availableRooms: clickedOffer.receipt?.maxAvailability ?? 10,
        affiliate: affiliateCode,
        maxAdultsPerRoom: determineMaxRoomCapacity([clickedOffer], sale?.tags).adults,
        travellersSelectionParam: isRelevantTravellersSelectionEnabled
          ? createQueryParamFromSelection(travellersSelection)
          : undefined,
      });
    };

    const handleTabClick = (tabValue: FlightsSelectOption) => {
      const isHotelWithFlights = tabValue === FlightsSelectOption.hotelWithFlights;
      setFlightsSelected(isHotelWithFlights);
      if (isHotelWithFlights) {
        void getFlightOptions(
          flightState.selectedAirport.id as string,
          flightState.selectedNights.id as number
        );
      }
      setURLParams('tripType', tabValue);
      setFinalRates(prev => ({ ...prev, flightsSelected: isHotelWithFlights }));
    };

    const handleCalendarCancelled = () => {
      if (!isSameDay(checkInDate, checkInDateParam)) {
        setCheckInDate(checkInDateParam);
      }
      if (!isSameDay(checkOutDate, checkOutDateParam)) {
        setCheckOutDate(checkOutDateParam);
      }

      changeOfferId(ALL_OFFERS);

      if (isMobile) {
        scrollToSection?.(!!checkInDateParam);
      }
    };

    const noAvailabilityReturned =
      !availability.loading && offerId === ALL_OFFERS && !availability.data?.checkInDates.length;
    const isLoading =
      (availability.loading && offerId === ALL_OFFERS && !receiptsData) || receiptsLoading;

    return (
      <Sentry.ErrorBoundary
        beforeCapture={scope => scope.setTag('error.boundary', 'avail-pres')}
        fallback={
          <RetryBoxWrapper data-testid="retry-box-avail-pres">
            <StyledRetryBox retryFunction={handleRetry} />
          </RetryBoxWrapper>
        }
      >
        <Presentation ref={presentationContainerRef}>
          <SelectionWrapper isMobile={isMobile}>
            {flightPriceAvailable && (
              <FlightsSelectTabs
                tripType={
                  flightsSelected
                    ? FlightsSelectOption.hotelWithFlights
                    : FlightsSelectOption.hotelOnly
                }
                onClick={handleTabClick}
                isAtolProtected={territoryCode === 'UK' && !isMobile}
              />
            )}
            <AvailabilitySearchBar
              availability={availability.data}
              offerOptions={offerOptions}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              onDatesChange={handleDatesChange}
              roomTypePlural={roomTypePlural}
              roomType={roomType}
              roomCapacity={determineMaxRoomCapacity(transformedOffers, sale?.tags)}
              numOfRooms={numOfRooms}
              setNumOfRooms={setNumOfRooms}
              isSameDates={isSameDates}
              focusedInput={focusedInput}
              setFocusedInput={setFocusedInput}
              onCalendarCancelled={handleCalendarCancelled}
              availabilityLoading={availability.loading}
              withTabs={flightPriceAvailable}
              withFlights={flightsSelected}
              flightState={flightState}
              onFlightStateChange={setFlightState}
              getFlightOptions={getFlightOptions}
              scrollToCalendar={scrollToSection}
              animateOffersSelect={animateOffersSelect}
              onAnimationChange={handleAnimationChange}
            />
          </SelectionWrapper>
          <CreditsStaffDiscountInfoBox />
          <Offers
            offers={transformedOffers}
            noAvailabilityReturned={noAvailabilityReturned}
            isLoading={isLoading}
            numberOfRooms={currentNumberOfRooms}
            onBookButtonClicked={onBookButtonClicked}
            openCalendar={openCalendar}
            flightsInfo={
              flightsSelected
                ? {
                    withFlightsDescription,
                    withFlightsOffers,
                  }
                : null
            }
          />
        </Presentation>
      </Sentry.ErrorBoundary>
    );
  }
);

interface SkeletonProps {
  flightPriceAvailable: boolean;
  showOfferDetailsContent: boolean;
  isMobile?: boolean;
}

const Skeleton = ({ flightPriceAvailable, showOfferDetailsContent, isMobile }: SkeletonProps) => {
  return (
    <Presentation data-testid="availability-skeleton">
      <SelectionWrapper isMobile={isMobile}>
        {flightPriceAvailable && <FlightsSelectTabs.Skeleton />}
        <AvailabilitySearchBar.Skeleton withTabs={flightPriceAvailable} isMobile={isMobile} />
      </SelectionWrapper>
      <OffersWrapper isMobile={isMobile}>
        <Offers.Skeleton
          showDetailsContent={showOfferDetailsContent}
          showPhoto={false}
          numberOfCards={4}
        />
      </OffersWrapper>
    </Presentation>
  );
};

AvailabilityPresentation.displayName = 'AvailabilityPresentation';
